import { Box, Flex, Grid, Heading, Text } from '@chakra-ui/react'
import { Btn } from '@/components/ui/Btn'
import { bookingUrl, SITE_CONFIG } from '@/config/site'
import { useLocale } from '@/i18n'

/** The accepted "split" hero: left column on the page ground, right column a solid accent panel. */
export const Hero = () => {
  const { locale } = useLocale()
  const { hero } = SITE_CONFIG[locale]

  return (
    <Grid
      as="header"
      id="top"
      templateColumns={{ base: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 0.62fr)' }}
      alignItems="stretch"
    >
      <Box
        px="clamp(20px, 4vw, 48px)"
        pt="clamp(32px, 5vw, 56px)"
        pb="clamp(32px, 4vw, 48px)"
      >
        <Flex
          align="baseline"
          gap="24px"
          flexWrap="wrap"
          borderBottom="2px solid"
          borderColor="divider"
          pb="14px"
        >
          <Text
            fontFamily="heading"
            fontWeight="800"
            fontSize="13px"
            letterSpacing="0.16em"
            textTransform="uppercase"
          >
            {hero.kicker}
          </Text>
          <Text fontSize="13px" letterSpacing="0.16em" textTransform="uppercase" color="accent.700">
            {hero.estLine}
          </Text>
        </Flex>

        <Heading
          as="h1"
          mt="clamp(32px, 4vw, 52px)"
          fontSize="clamp(34px, 5.4vw, 100px)"
          fontWeight="800"
          lineHeight="0.98"
          letterSpacing="-0.035em"
        >
          {hero.headline}
          <Box as="span" color="accent">
            {hero.headlineAccent}
          </Box>
        </Heading>

        <Text mt="clamp(24px, 3vw, 40px)" fontSize="17px" lineHeight="1.6" maxW="56ch">
          {hero.subheadline}
        </Text>

        <Flex
          gap="clamp(18px, 3vw, 40px)"
          flexWrap="wrap"
          mt="clamp(32px, 4vw, 48px)"
          borderTop="2px solid"
          borderColor="divider"
          pt="18px"
        >
          {hero.trustMarkers.map((marker) => (
            <Text
              key={marker}
              fontFamily="heading"
              fontWeight="800"
              fontSize="13px"
              letterSpacing="0.16em"
              textTransform="uppercase"
            >
              {marker}
            </Text>
          ))}
        </Flex>
      </Box>

      <Flex
        direction="column"
        bg="accent"
        color="ground"
        px="clamp(20px, 3.4vw, 40px)"
        pt="clamp(32px, 5vw, 56px)"
        pb="clamp(32px, 4vw, 48px)"
      >
        <Text fontSize="13px" letterSpacing="0.16em" textTransform="uppercase" opacity={0.8}>
          {hero.processLine}
        </Text>
        <Text
          mt="clamp(24px, 3vw, 40px)"
          fontFamily="heading"
          fontWeight="800"
          fontSize="clamp(22px, 2.1vw, 34px)"
          lineHeight="1.14"
          letterSpacing="-0.02em"
        >
          {hero.bridgeLine}
        </Text>
        <Flex
          direction="column"
          gap="10px"
          mt={{ base: '32px', lg: 'auto' }}
          pt={{ base: '0', lg: '48px' }}
        >
          <Btn
            href={bookingUrl('build', 'hero')}
            target="_blank"
            rel="noopener noreferrer"
            variant="ground"
            block
            fontSize="16px"
            px="20px"
            py="18px"
          >
            {hero.ctaLabel}
          </Btn>
          <Btn
            href={bookingUrl('ai-audit', 'hero')}
            target="_blank"
            rel="noopener noreferrer"
            variant="groundOutline"
            block
            fontSize="16px"
            px="20px"
            py="18px"
          >
            {hero.secondaryCtaLabel}
          </Btn>
        </Flex>
      </Flex>
    </Grid>
  )
}
